// Security & Audit: shapes of /api/security/* and /api/loans/{id}/assignments (docs/AUDIT-TRAIL.md),
// plus pure display helpers. The server decides access and computes everything; this only formats.
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import type { Role } from "./types";

export type AuditAction = "view" | "list" | "export" | "download" | "write" | "grant" | "revoke";
export type AuditOutcome = "allowed" | "denied" | "error";

export const AUDIT_ACTIONS: AuditAction[] = ["view", "list", "export", "download", "write", "grant", "revoke"];

export interface PolicyRole {
  role: Role | string;
  label: string;
  loan_scope: string;
  capabilities: string[];
  assignable: boolean;
  status: "existing" | "pilot_default";
  owner_question: string | null;
}

export interface SecurityUser {
  id: string;
  display_name: string;
  role: Role | string;
  status: string;
  team_id: string | null;
  team_name: string | null;
}

export interface Assignment {
  loan_id: string;
  user_id: string;
  assignment_role: string;
  granted_by: string | null;
  granted_at: string;
}

export interface AccessChange {
  seq: number;
  occurred_at: string;
  actor_id: string | null;
  action: "grant" | "revoke";
  resource_type: "assignment" | "user_role" | "user_status" | string;
  resource_id: string | null;
  item_ids: string[];
}

export interface SecurityAccess {
  policy_version: number;
  scope: "all" | "teams";
  roles: PolicyRole[];
  loan_scopes: Record<string, string>;
  users: SecurityUser[];
  assignments: Assignment[];
  /** Active acting-role grants of the people listed (migration 015). */
  acting_seats?: { user_id: string; role: string }[];
  changes: AccessChange[];
}

export interface AuditRow {
  seq: number;
  occurred_at: string;
  actor_id: string | null;
  actor_role: string;
  client: string;
  action: AuditAction;
  resource_type: string;
  resource_id: string | null;
  operation: string;
  item_count: number | null;
  outcome: AuditOutcome;
  reason: string | null;
  http_status: number | null;
  request_id: string;
  row_hash: string;
}

export interface LoanChange {
  loan_id: string;
  field: string;
  actor_id: string | null;
  source: string;
  at: string;
}

export interface AuditPage {
  rows: AuditRow[];
  next_before_seq: number | null;
  loan_changes: LoanChange[];
}

export interface FlagRule {
  id: string;
  label: string;
  enabled: boolean;
  threshold: string | null;
  explanation: string;
}

export interface Flag {
  rule: string;
  actor_id: string | null;
  actor_role: string;
  first_at: string;
  last_at: string;
  count: number;
  sample_seqs: number[];
  detail: string;
}

export interface FlagsResponse {
  rules: FlagRule[];
  flags: Flag[];
}

export interface ChainStatus {
  ok: boolean;
  rows: number;
  head_seq: number | null;
  head_hash: string | null;
  first_bad_seq: number | null;
  checked_at: string;
}

export interface Protection {
  append_only_triggers: boolean;
  service_role_can_update: boolean;
  service_role_can_delete: boolean;
  table_owner_is_service_role: boolean;
  /** Dev databases: the app connects as a superuser, so only the triggers enforce append-only. */
  service_role_is_superuser?: boolean;
}

export interface RestoreDrill {
  format: string;
  performed_at: string;
  system: string;
  result: string;
  method: string;
  detail: string;
  operator_role: string;
}

export interface Compliance {
  format: string;
  exported_at: string;
  subscription_label: string;
  summary: {
    policy_noncompliant_resources: number | null;
    policy_noncompliant_policies: number | null;
    secure_score_current: number | null;
    secure_score_max: number | null;
    secure_score_percentage: number | null;
  };
  compliance_manager_score: number | null;
}

export interface SecurityStatus {
  chain: ChainStatus;
  protection: Protection;
  restore_drill: RestoreDrill | null;
  compliance: Compliance | null;
}

export interface LoanAssignment {
  user_id: string;
  display_name: string;
  assignment_role: string;
  granted_by: string | null;
  granted_at: string;
}

export interface AuditFilters {
  actor?: string;
  loan?: string;
  action?: AuditAction | "";
  deniedOnly?: boolean;
  since?: string; // YYYY-MM-DD (local org day; sent as start of day UTC)
  until?: string; // YYYY-MM-DD, inclusive
  beforeSeq?: number | null;
  limit?: number;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(s: string): boolean {
  return UUID.test(s.trim());
}

/** The query string for GET /api/security/audit. Invalid or empty filters are left out. */
export function auditQuery(f: AuditFilters): string {
  const p = new URLSearchParams();
  if (f.actor && isUuid(f.actor)) p.set("actor", f.actor.trim().toLowerCase());
  if (f.loan && isUuid(f.loan)) p.set("loan", f.loan.trim().toLowerCase());
  if (f.action) p.set("action", f.action);
  if (f.deniedOnly) p.set("outcome", "denied");
  if (f.since && /^\d{4}-\d{2}-\d{2}$/.test(f.since)) p.set("since", `${f.since}T00:00:00Z`);
  if (f.until && /^\d{4}-\d{2}-\d{2}$/.test(f.until)) p.set("until", `${nextDay(f.until)}T00:00:00Z`);
  if (f.beforeSeq != null) p.set("before_seq", String(f.beforeSeq));
  p.set("limit", String(f.limit ?? 100));
  return p.toString();
}

function nextDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** First 8 characters of a UUID or hash, for compact display. */
export function shortId(id: string | null | undefined): string {
  if (!id) return "—";
  return id.length > 8 ? id.slice(0, 8) : id;
}

/** Staff display name for a user id; ids the viewer can't resolve stay as a short id. */
export function actorName(id: string | null, users: Map<string, SecurityUser>): string {
  if (!id) return "Not signed in";
  return users.get(id)?.display_name ?? `User ${shortId(id)}`;
}

/** A resource reference for the log: never loan contents, only the type and a short id. */
export function resourceLabel(type: string, id: string | null, users: Map<string, SecurityUser>): string {
  const t = type.replace(/_/g, " ");
  if (!id) return t;
  if (id.startsWith("scope:")) return `${t} (${id.slice(6)})`;
  if (["user_role", "user_status", "user", "passkey", "mfa_verification", "mfa_enrollment", "mfa_duo_start"].includes(type)) {
    const u = users.get(id);
    if (u) return `${t}: ${u.display_name}`;
  }
  return `${t} ${shortId(id)}`;
}

export function outcomeTone(o: AuditOutcome): "ok" | "bad" | "warn" {
  return o === "allowed" ? "ok" : o === "denied" ? "bad" : "warn";
}

export const ACTION_LABEL: Record<AuditAction, string> = {
  view: "Viewed",
  list: "Listed",
  export: "Exported",
  download: "Downloaded",
  write: "Changed",
  grant: "Granted",
  revoke: "Revoked",
};

export function ruleStatus(r: FlagRule): string {
  if (!r.enabled) return "Rule off — the owner has not set a threshold";
  return r.threshold ? `On · threshold ${r.threshold}` : "On";
}

export function chainSummary(c: ChainStatus): { ok: boolean; title: string; detail: string } {
  if (c.ok) {
    return {
      ok: true,
      title: "Intact",
      detail: c.rows ? `${c.rows.toLocaleString("en-US")} rows verified; head #${c.head_seq} ${shortId(c.head_hash)}` : "No rows yet",
    };
  }
  return {
    ok: false,
    title: c.first_bad_seq != null ? `Broken at row #${c.first_bad_seq}` : "Broken",
    detail: `${c.rows.toLocaleString("en-US")} rows checked. A changed, missing or reordered row was detected.`,
  };
}

/** Facts about the audit table's protection, each red when it weakens append-only. */
export function protectionFacts(p: Protection): { label: string; ok: boolean }[] {
  return [
    { label: p.append_only_triggers ? "Append-only triggers in place" : "Append-only triggers missing", ok: p.append_only_triggers },
    { label: p.service_role_can_update ? "App role can UPDATE rows" : "App role cannot UPDATE rows", ok: !p.service_role_can_update },
    { label: p.service_role_can_delete ? "App role can DELETE rows" : "App role cannot DELETE rows", ok: !p.service_role_can_delete },
    {
      label: p.table_owner_is_service_role ? "App role owns the table (could disable triggers)" : "Table owned by a separate role",
      ok: !p.table_owner_is_service_role,
    },
    ...(p.service_role_is_superuser
      ? [{ label: "Dev database: service role is superuser, append-only enforced by triggers only", ok: false }]
      : []),
  ];
}

/** Current month as YYYY-MM in UTC, for the export picker default. */
export function currentMonth(now: Date = new Date()): string {
  return now.toISOString().slice(0, 7);
}

export function isMonth(s: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(s);
}

// ── Queries ──────────────────────────────────────────────────────────────────

export const securityKeys = {
  access: ["security", "access"] as const,
  status: ["security", "status"] as const,
  flags: (days: number) => ["security", "flags", days] as const,
  audit: (q: string) => ["security", "audit", q] as const,
  assignments: (loanId: string) => ["assignments", loanId] as const,
};

export function useSecurityAccess(enabled = true) {
  return useQuery({ queryKey: securityKeys.access, queryFn: () => api<SecurityAccess>("/api/security/access"), enabled, staleTime: 30_000 });
}

export function useSecurityStatus() {
  return useQuery({ queryKey: securityKeys.status, queryFn: () => api<SecurityStatus>("/api/security/status"), staleTime: 30_000 });
}

export function useSecurityFlags(days: number) {
  return useQuery({ queryKey: securityKeys.flags(days), queryFn: () => api<FlagsResponse>(`/api/security/flags?days=${days}`), staleTime: 30_000 });
}

export function useAuditLog(query: string) {
  return useQuery({ queryKey: securityKeys.audit(query), queryFn: () => api<AuditPage>(`/api/security/audit?${query}`), staleTime: 10_000 });
}

export function useLoanAssignments(loanId: string) {
  return useQuery({
    queryKey: securityKeys.assignments(loanId),
    queryFn: () => api<LoanAssignment[]>(`/api/loans/${loanId}/assignments`),
    staleTime: 30_000,
  });
}

/** Active assignments per user, and which roles are assignable (for the People page). */
export function assignmentCounts(access: Pick<SecurityAccess, "roles" | "assignments"> | undefined): { byUser: Map<string, number>; assignable: Set<string> } | null {
  if (!access) return null;
  const byUser = new Map<string, number>();
  for (const a of access.assignments) byUser.set(a.user_id, (byUser.get(a.user_id) ?? 0) + 1);
  return { byUser, assignable: new Set(access.roles.filter((r) => r.assignable).map((r) => r.role)) };
}
